import { useEffect, useState, Fragment, useMemo } from 'react';
import { toast } from 'react-toastify';
import {
  Client,
  ChargeType,
  ClientRateCardInput,
  MinimumInput,
} from '@core/generated/gql-types';
import { useClientStore } from '@core/client/useClientStore';
import { useClassificationStore } from '@core/classification/useClassificationStore';
import { useCoreLoopConfigurationStore } from '@core/coreLoopConfiguration/useCoreLoopConfigurationStore';
import type { ICoreLoopConfigurationItems } from '@core/coreLoopConfiguration/coreLoopConfiguration.types';
import { Listbox, Transition } from '@headlessui/react';
import { ChevronUpDownIcon, CheckIcon } from '@heroicons/react/20/solid';
import {
  clientChargeTypeOptions,
  formatChargeTypeLabel,
} from '@/utils/client.constants';
import { ClientRatesBody } from './ClientRatesBody';
import { filterAndSortClassificationGroupsForClient } from './clientRates.utils';

interface ClientRatesProps {
  client: Client;
  onUpdateClient?: (
    updatedClient: Partial<Client>,
  ) => Promise<{ success: boolean; error?: string } | void>;
}

const ClientRates = ({ client, onUpdateClient }: ClientRatesProps) => {
  const { fetchClientById, setClientRateCard } = useClientStore();

  const {
    classificationGroups,
    fetchClassificationGroups,
    fetchClassificationLevelClientRates,
    getClassificationLevelRatesForClient,
    setClassificationLevelClientRate,
  } = useClassificationStore();

  const { coreLoopConfiguration, fetchAndSetCoreLoopConfiguration } =
    useCoreLoopConfigurationStore();

  const chargeType = client.chargeType || ChargeType.Margin;
  const [isUpdatingChargeType, setIsUpdatingChargeType] = useState(false);
  const [isLoadingFixedRates, setIsLoadingFixedRates] = useState(false);

  const tenantMarginPercentage = useMemo(() => {
    return coreLoopConfiguration?.configuration?.marginPercentage ?? null;
  }, [coreLoopConfiguration?.configuration?.marginPercentage]);

  const listboxChargeTypeOptions = useMemo(() => {
    const configuration = coreLoopConfiguration?.configuration as
      Partial<ICoreLoopConfigurationItems> | undefined;

    const allowedOptions = clientChargeTypeOptions.filter((option) => {
      if (
        option === ChargeType.Fixed &&
        configuration?.allowClientFixedChargeType === false
      ) {
        return false;
      }
      if (
        option === ChargeType.RateCard &&
        configuration?.allowClientRateCardChargeType === false
      ) {
        return false;
      }
      return true;
    });

    const allowedSet = new Set(allowedOptions);
    if (!allowedSet.has(chargeType)) {
      allowedSet.add(chargeType);
    }

    return clientChargeTypeOptions.filter((option) => allowedSet.has(option));
  }, [chargeType, coreLoopConfiguration?.configuration]);

  // Only use rates that have been explicitly saved (have id) - exclude any template/default
  // data the backend might return for clients with no saved fixed rates
  const clientRates = getClassificationLevelRatesForClient(
    client.id || '',
  ).filter((rate) => rate?.id != null && rate.id !== '');

  const clientClassificationGroups = useMemo(
    () =>
      filterAndSortClassificationGroupsForClient(
        classificationGroups,
        client.classifications,
      ),
    [classificationGroups, client.classifications],
  );

  useEffect(() => {
    if (client.tenantId) {
      fetchAndSetCoreLoopConfiguration(client.tenantId);
    }
  }, [client.tenantId, fetchAndSetCoreLoopConfiguration]);

  useEffect(() => {
    if (client.tenantId) {
      fetchClassificationGroups(client.tenantId);
    }
  }, [client.tenantId, fetchClassificationGroups]);

  useEffect(() => {
    if (chargeType === ChargeType.Fixed && client.id) {
      setIsLoadingFixedRates(true);
      fetchClassificationLevelClientRates(client.id).finally(() =>
        setIsLoadingFixedRates(false),
      );
    }
  }, [chargeType, client.id, fetchClassificationLevelClientRates]);

  useEffect(() => {
    if (chargeType === ChargeType.RateCard && client.id) {
      void fetchClientById(client.id);
    }
  }, [chargeType, client.id, fetchClientById]);

  const handleChargeTypeChange = async (newChargeType: ChargeType) => {
    if (newChargeType === chargeType || !onUpdateClient) return;
    setIsUpdatingChargeType(true);
    try {
      await onUpdateClient({ chargeType: newChargeType });
    } catch (error) {
      console.error('Error updating charge type:', error);
      toast.error('Failed to update charge type');
    } finally {
      setIsUpdatingChargeType(false);
    }
  };

  const handleSaveClientRateCard = async (rateCard: ClientRateCardInput) => {
    if (!client.id) {
      return { success: false, error: 'No client ID' };
    }
    return setClientRateCard(client.id, rateCard);
  };

  const handleSaveFixedRate = async (
    classificationLevelId: string,
    minimums: MinimumInput[],
  ) => {
    if (!client.id) return { success: false, error: 'No client ID' };
    const result = await setClassificationLevelClientRate(
      classificationLevelId,
      client.id,
      minimums,
    );
    if (result.success && client.id) {
      await fetchClassificationLevelClientRates(client.id);
    }
    return result;
  };

  const getChargeTypeBadgeClass = (type: ChargeType) => {
    switch (type) {
      case ChargeType.Margin:
        return 'bg-blue-100 text-blue-800';
      case ChargeType.Fixed:
        return 'bg-purple-100 text-purple-800';
      case ChargeType.RateCard:
        return 'bg-teal-100 text-teal-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="p-4">
      <div className="mb-4 flex items-center gap-3">
        <h2 className="text-2xl font-bold">{client.name} Rates</h2>
        <Listbox
          value={chargeType}
          onChange={handleChargeTypeChange}
          disabled={isUpdatingChargeType || !onUpdateClient}
        >
          {({ open }) => (
            <div className="relative">
              <Listbox.Button
                className={`inline-flex cursor-pointer rounded-full px-2.5 py-0.5 text-xs font-semibold focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:outline-none disabled:cursor-not-allowed disabled:opacity-50 ${getChargeTypeBadgeClass(chargeType)}`}
              >
                <span>{formatChargeTypeLabel(chargeType)}</span>
                <ChevronUpDownIcon
                  className="ml-1 h-3 w-3 text-current"
                  aria-hidden="true"
                />
              </Listbox.Button>
              <Transition
                show={open}
                as={Fragment}
                leave="transition ease-in duration-100"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
              >
                <Listbox.Options className="absolute z-10 mt-1 min-w-36 overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black/5 focus:outline-none sm:text-sm">
                  {listboxChargeTypeOptions.map((option) => (
                    <Listbox.Option
                      key={option}
                      value={option}
                      className={({ active }) =>
                        `relative cursor-pointer py-2 pr-4 pl-8 select-none ${
                          active ? 'bg-blue-100 text-blue-900' : 'text-gray-900'
                        }`
                      }
                    >
                      {({ selected }) => (
                        <>
                          <span
                            className={selected ? 'font-medium' : 'font-normal'}
                          >
                            {formatChargeTypeLabel(option)}
                          </span>
                          {selected && (
                            <span className="absolute inset-y-0 left-0 flex items-center pl-2 text-blue-600">
                              <CheckIcon
                                className="h-4 w-4"
                                aria-hidden="true"
                              />
                            </span>
                          )}
                        </>
                      )}
                    </Listbox.Option>
                  ))}
                </Listbox.Options>
              </Transition>
            </div>
          )}
        </Listbox>
      </div>

      <ClientRatesBody
        chargeType={chargeType}
        client={client}
        classificationGroups={clientClassificationGroups}
        tenantMarginPercentage={tenantMarginPercentage}
        clientRates={clientRates}
        isLoadingFixedRates={isLoadingFixedRates}
        onSaveFixedRate={handleSaveFixedRate}
        onSaveClientRateCard={handleSaveClientRateCard}
        isRatesEditingDisabled={!onUpdateClient}
      />
    </div>
  );
};

export default ClientRates;
